/**
 * Transcribe Page
 * Main page for transcribing video streams from URL
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Stack, useTheme, spacing, textStyles, fontSize } from '@fti/design-system';
import { MatterSelect } from '../components/echo/MatterSelect';
import { WorkflowForm, WorkflowFormData } from '../components/echo/WorkflowForm';
import { WorkflowSuccessMessage } from '../components/echo/WorkflowSuccessMessage';
import { useWorkflowDispatch } from '../hooks/useWorkflowDispatch';
import { useEchoUser } from '../hooks/useEchoUser';
import { parseKickVideoIdentifiers, detectPlatform } from '../utils/urlParser';
import { resolvePromptInputs } from '../config/echo';
import type { EchoMatter } from '../api/types/echo';
import type { WorkflowFormInput } from '../hooks/useWorkflowDispatch';

/**
 * Page component for transcribing video streams
 */
export function TranscribePage() {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const { user } = useEchoUser();
  const workflow = useWorkflowDispatch();

  const [selectedMatter, setSelectedMatter] = useState<{
    id: number | null;
    matter: EchoMatter | null;
  }>({ id: null, matter: null });

  const handleMatterChange = useCallback((matterId: number | null, matter: EchoMatter | null) => {
    setSelectedMatter({ id: matterId, matter });
  }, []);

  const handleFormSubmit = useCallback(async (formData: WorkflowFormData) => {
    if (!selectedMatter.id || !user?.email) {
      return;
    }

    // Parse the video URL
    const platform = detectPlatform(formData.videoUrl);
    const kickIds = platform === 'kick' ? parseKickVideoIdentifiers(formData.videoUrl) : null;

    // Resolve prompt inputs based on the selected prompt mode
    const { promptTemplate, flaggedTerms } = resolvePromptInputs({
      promptMode: formData.promptMode,
      customPrompt: formData.customPrompt,
      promptTemplate: formData.promptTemplate,
      flaggedTerms: formData.flaggedTerms,
      streamerName: formData.streamerName,
    });

    // Build workflow input
    const workflowInput: WorkflowFormInput = {
      url: formData.videoUrl,
      streamerName: formData.streamerName || kickIds?.streamerName || '',
      date: formData.vodDate,
      promptTemplate,
      flaggedTerms,
      emailRecipients: formData.emailRecipients,
      language: formData.language,
    };

    // Use the matter ID as streamer ID for now (needs proper streamer selection)
    await workflow.dispatch(workflowInput, selectedMatter.id, user.email);
  }, [selectedMatter.id, user?.email, workflow]);

  const handleReset = useCallback(() => {
    workflow.reset();
  }, [workflow]);

  const handleViewHistory = useCallback(() => {
    navigate('/history');
  }, [navigate]);

  const textColor = theme.colors.text.primary;
  const mutedColor = theme.colors.text.secondary;

  // Check if form can be submitted
  const canSubmit = !!selectedMatter.id && !workflow.isDispatching && !workflow.isPolling;

  return (
    <Stack gap="lg">
      {/* Page Header */}
      <div>
        <h1 style={{ margin: 0, ...textStyles.h3, color: textColor }}>
          Transcribe and Summarize
        </h1>
        <p style={{ margin: 0, marginTop: spacing.xs, ...textStyles.body, color: mutedColor }}>
          Enter a video URL to start transcription and summarization workflow
        </p>
        <p style={{ margin: 0, marginTop: spacing.xs, fontSize: fontSize.sm, color: mutedColor }}>
          Supported URLs: Kick, YouTube, Twitch, Apple Podcasts, GetTR
        </p>
      </div>

      {/* Main Form Card */}
      <Card variant="elevated">
        <Card.Body>
          <Stack gap="lg">
            {/* Matter Selection */}
            <MatterSelect
              value={selectedMatter.id}
              onChange={handleMatterChange}
              required
              label="Select Matter"
              placeholder="Choose a matter for billing..."
              showBillingRate={false}
            />

            {/* Workflow Form */}
            <WorkflowForm
              onSubmit={handleFormSubmit}
              isSubmitting={workflow.isDispatching || workflow.isPolling}
              defaultPrompt=""
              showUrlField={true}
            />

            {/* No matter selected warning */}
            {!selectedMatter.id && (
              <div
                style={{
                  padding: spacing.sm,
                  background: `${theme.colors.semantic.warning.light}20`,
                  border: `1px solid ${theme.colors.semantic.warning.light}`,
                  borderRadius: '6px',
                  fontSize: fontSize.sm,
                  color: theme.colors.semantic.warning.default,
                }}
              >
                Please select a matter before submitting the workflow.
              </div>
            )}
          </Stack>
        </Card.Body>
      </Card>

      {/* Workflow Status */}
      {(workflow.isDispatching || workflow.isPolling || workflow.run || workflow.error) && (
        <WorkflowSuccessMessage
          isDispatching={(workflow.isDispatching || workflow.isPolling) && !workflow.run}
          isSuccess={!!workflow.run && !workflow.error}
          error={workflow.error}
          onReset={handleReset}
          onViewHistory={handleViewHistory}
        />
      )}

      {/* Help Section */}
      <Card variant="outlined">
        <Card.Body>
          <Stack gap="sm">
            <h3 style={{ margin: 0, ...textStyles.bodySmall, fontWeight: textStyles.label.fontWeight, color: textColor }}>
              Supported Platforms
            </h3>
            <ul
              style={{
                margin: 0,
                paddingLeft: spacing.lg,
                color: mutedColor,
                fontSize: fontSize.sm,
              }}
            >
              <li>
                <strong>Kick</strong> - https://kick.com/video/UUID or https://kick.com/channel/videos/UUID
              </li>
              <li>
                <strong>YouTube</strong> - https://youtube.com/watch?v=VIDEO_ID or https://youtu.be/VIDEO_ID
              </li>
              <li>
                <strong>Twitch</strong> - https://twitch.tv/videos/VOD_ID
              </li>
              <li>
                <strong>Apple Podcasts</strong> - https://podcasts.apple.com/us/podcast/episode-name/id123456?i=1000123456
              </li>
              <li>
                <strong>GetTR</strong> - https://gettr.com/...
              </li>
            </ul>
          </Stack>
        </Card.Body>
      </Card>
    </Stack>
  );
}

export default TranscribePage;
